import { BadInputError } from '../errors/BadInputError';
import {
  METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN,
  METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN_LABEL,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_END_DATE,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_START_DATE,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_SUPPLIER_NAME,
  isMethodologyCustomizationValueMappingConfigId,
} from '../methodologyCustomization/methodologyCustomizationConstants';
import assertNever from '../utils/assertNever';
import {
  FINANCIALS_CONFIG_ID,
  SUPPLIER_COMPANY_CONFIG_ID,
  VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_DESCRIPTION,
  VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME,
  VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND,
  VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID,
  VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA,
  VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_DATASET_KIND,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE,
  VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY,
  VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME,
  isMappingKindConfigId,
} from './hardcodedValueMappingConfigs';
import {
  VALUE_MAPPING_OUTPUT_OPTION_KEY_EXCLUDE,
  VALUE_MAPPING_VENDOR_SAMPLE_COL,
  ValueMappingRecordWithId,
  ValueMappingRow,
} from './valueMappingTypes';
import {
  GQCanonicalDatasetKind,
  GQValueMappingOutputOption,
} from '../generated/graphql';
import { DateTime } from 'luxon';
import isDate from 'lodash/isDate';
import { SUPPLIERS_REQUIRED } from '../CanonicalDatasetUtils';

export type ValueMappingColumnLabelOverrides = { [key: string]: string };
export type ValueMappingColumnFlexSizeOverrides = { [key: string]: number };
export type ValueMappingColumnPickerKeyOverrides = {
  [key: string]: (pickerKey: string) => string | null;
};

export type ValueMappingCellOptInSettings = {
  // keeping this separate and required so we can turn on/off this feature quickly without deleting
  // all related code if needed and not having to do weird null checks for boolean
  isEnabled: boolean;
  toggledOffLabel?: string;
  toggledOffButtonTooltip?: string;
  toggledOffLabelTooltip?: string;
  toggledOnButtonTooltip?: string;
  relatedColumnNames: Array<string>;
};

export type ValueMappingTableDetailsConfiguration = {
  /**
   * If you do not set this, it will render all of the non-hidden key column values, separated by middot.
   * Override by setting as a column key to use the value instead.
   */
  detailViewHeaderTitleColumn?: string;
  /**
   * The subtitle is under the title. If not set, nothing will render.
   * Override by setting as a column key to use the value instead.
   */
  detailViewHeaderSubtitleColumn?: string;
  /**
   * If enabled, the detail view will preview the first 20 rows of each file that match the filter settings provided, sorted by the sort settings provided.
   */
  detailViewFileDataPreview?: {
    filterSettings: Array<{
      columnNames: Array<string>; // the column names that exists in the parsed file (used in backend when parsing file) (e.g. 'account_id' and 'account_name'). If multiple columns are provided, the filter will be applied as an or condition for each of these.
      valueGetter: (row: ValueMappingRecordWithId) => string; // a function that returns the value to filter by (e.g. (row) => row.financial_account_id). Only supporting strings right now to handle gql types
    }>;
    sortSettings: string; // the column to sort by (e.g. 'amount_spend')
  };
};

export type ValueMappingOutputValidationResult = {
  invalidOptions: Array<string>;
  warnings: Array<{ outputOptionId: string; label: string }>;
};

export type ValueMappingOutputValidator = (
  row: ValueMappingRecordWithId,
  outputOptions: Array<GQValueMappingOutputOption>
) => ValueMappingOutputValidationResult | undefined;

/**
 * For certain mapping configurations, we want to return lists of invalid or
 * "use with caution" items - this type is for those use cases.
 */
export type OptionsForRowHandler = (
  row: ValueMappingRecordWithId,
  options: Array<ValueMappingRow>
) => Array<string>;

export type ValueMappingTableConfiguration = {
  configId: string;
  /**
   * Enables async loading for options on specified output columns in the value mapping table UI.
   * If this boolean is set to true for a column, you must also provide
   * a method for your config's column in ValueMappingService.getValueMappingOutputOptions.
   *
   * Ex: { [VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID]: true}
   */
  asyncLoadOutputOptionsForColumn?: Record<string, boolean>;

  /**
   * In the frontend, we have a dropdown, which shows items' `id` in the
   * subtitle. You might want to customize that string — perhaps trimming a
   * prefix, or removing it altogether. Here's where you do it.
   *
   * Object is keyed by column name.
   */
  columnPickerKeyOverrides?: ValueMappingColumnPickerKeyOverrides | undefined;

  /**
   * Default would be capitalized column name:
   * ex. 'amount_spend' -> 'Amount spend'
   *
   * Override by setting custom labels here. Object is keyed by column name.
   */
  columnLabelOverrides?: ValueMappingColumnLabelOverrides;

  /**
   * Default is flex == 1:
   * ex. 'financial_account_name' -> 2
   *
   * Override by setting custom sizes here. Object is keyed by column name.
   * NOTE: this only applies to key/context columns right now. Could extend to output if needed.
   */
  columnFlexSizeOverrides?: ValueMappingColumnFlexSizeOverrides;

  /**
   * Used to sort the mapping by most important rows at the top for first render
   * E.g. columns: [{name: 'amount_spend', direction: 'desc', absolute: true}, {name: 'account_name',  direction: 'asc'}]
   * => ORDER BY abs(amount_spend) DESC, account_name ASC
   */
  defaultSorting?: Array<{
    name: string;
    direction: 'asc' | 'desc';
    absolute?: boolean;
  }>;

  /**
   * The detail view is a sidebar that opens up to show information about the row.
   * This is only needed if you have isDetailsViewEnabled set to true.
   */
  detailsViewSettings?: ValueMappingTableDetailsConfiguration;

  /**
   * If set, a row can be made opt-in by your given logic.
   * If opt-in, ALL output columns for the row will render as disabled until user
   * clicks that they wish to set the value.
   *
   * To use, you must also provide userOverrideCells and setUserOverrideCells to ValueMappingTableWrapper!
   *
   * Ex. For financial mappings, a row is optional if the
   * vendor_entity column is not null.
   */
  getCellOptInSettings?: (
    row: ValueMappingRecordWithId,
    columnName: string
  ) => ValueMappingCellOptInSettings | null;

  /**
   * Returns a list of output option IDs with information about whether/how they
   * should be displayed in the output picker. Invalid options are not shown,
   * and warning options get a yellow triangle.
   */
  validateOutputOptions?: ValueMappingOutputValidator;

  /**
   * If true, users will be able to flag all rows as needing attention, either
   * by themselves, or by others for review.
   *
   * !! To fully enable, you MUST add to config output column of:
   * type string (ValueMappingFlagMode) and name VALUE_MAPPING_SPECIAL_COLUMN_NAME_FLAGGED_ROW!
   * This output column does NOT get added to the BART.
   */
  hasRowFlagging?: boolean;

  /**
   * If true, "exclude" option gets added to every picker in every row.
   * When selected, the should exclude option is selected.
   * A config will need this option if the user needs a way to mark certain ingested rows
   * as excluded from the footprint. Ex. duplicated rows and things like taxes that never count towards footprint
   *
   * !! To fully enable, you MUST add to config output column of:
   * type boolean and name VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE!
   */
  specialOptions?: Array<GQValueMappingOutputOption>;

  /**
   * In order to support vendor overrides showing up nested in your rows,
   * you need to not only set this method but add to your mapping config the column:
   * VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY
   * and set up the tree structure with MAPPING_NESTED_TREE_PATH_COL
   *
   * In the future, we may want to differentiate between vendor overrides
   * and more general tree structure support, but not until we have a use case.
   */
  hasVendorOverrides?: boolean;

  /**
   * The detail view is a sidebar that opens up to show information about the row.
   * Do not set if you don't wish to use the sidebar.
   */
  isDetailsViewEnabled?: boolean;

  /**
   * Enables or disables the changelog within the details view. `isDetailsViewEnabled` must be
   * true for this to have any effect. If this is false, details view will still show files
   * + comments.
   */
  isHistoryEnabled?: boolean;

  /**
   * Row labels are used as header over the table, the unique row identifier but readable
   * Ex. "100 Accounts"
   */
  rowLabel?: string;

  /**
   * If provided, server-side searches for userSearchText in these columns
   * e.g. ['bea', 'account_name']
   * Returns all rows where the search text appears in any of the columns
   */
  searchableColumnNames?: Array<string>;
  /*
   * Given a value mapping row, determine if it is optional. This will override isRequiredForPublish column checks
   */
  isRowOptional?: (row: ValueMappingRecordWithId) => boolean;
  isRequiredFilterEnabled?: boolean;
  primaryKeyColumn?: string;
  enableDownload?: boolean;
  /**
   * if true, users can get access to a generate suggestions button on the value mapping.
   * Currently only financial mappings has this feature. Please hook up to the backend if you want to use this feature
   * for other value mappings.
   */
  isSuggestionsEnabled?: boolean;
};

export const FinancialsConfiguration: ValueMappingTableConfiguration = {
  configId: FINANCIALS_CONFIG_ID,
  columnPickerKeyOverrides: {
    [VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA]: (pickerKey: string) => {
      // If the key starts with naics_, strip it because it's confusing to user
      // But leave it in the key for BE use!
      return pickerKey.replace('naics_', '');
    },
  },
  columnLabelOverrides: {
    [VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA]: 'Industry code',
    [VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID]: 'Account ID',
    [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME]:
      'Account name',
    [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_DESCRIPTION]:
      'Account description',
    [VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY]: 'Suppliers',
    [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND]:
      'Aggregated spend (approx.)',
    // This is a bit hacky, but it's to hide this column from showing up in the search
    // tooltip since it's kind of a "special" secret column that supplements suppliers
    [VALUE_MAPPING_VENDOR_SAMPLE_COL]: '',
  },
  columnFlexSizeOverrides: {
    [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME]: 2,
  },
  // Sort so the biggest sums are at the top (they're the "most important" to map)
  defaultSorting: [
    {
      name: VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND,
      direction: 'desc',
      absolute: true,
    },
  ],
  detailsViewSettings: {
    detailViewHeaderTitleColumn:
      VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME,
    detailViewFileDataPreview: {
      filterSettings: [
        {
          columnNames: [
            'account_id',
            'account_name',
            'account_name_or_expense_type',
          ],
          valueGetter: (row) =>
            row[
              VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID
            ] as string,
        },
        {
          columnNames: ['vendor', 'vendor_entity'],
          valueGetter: (row) =>
            row[
              VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY
            ] as string,
        },
      ],
      sortSettings: 'cost',
    },
  },
  getCellOptInSettings: (
    row: ValueMappingRecordWithId,
    _columnName: string
  ) => {
    if (row[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY] == null) {
      return null;
    }
    return {
      isEnabled: true,
      toggledOffLabel: 'Linked to account',
      toggledOffButtonTooltip: 'Click to override value for this supplier',
      toggledOffLabelTooltip:
        'This supplier will match the default industry code for this account ID.',
      toggledOnButtonTooltip:
        'Click to remove supplier override and use default value instead',
      relatedColumnNames: [VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE],
    };
  },

  hasRowFlagging: true,
  specialOptions: [
    {
      id: VALUE_MAPPING_OUTPUT_OPTION_KEY_EXCLUDE,
      title: 'Non-emissive spend',
      subtitle:
        'By excluding this item from your mapping, this item will not appear in your footprint.',
      descriptionLong: null,
      isSuggested: false,
      category: null,
      extraData: {},
    },
  ],
  hasVendorOverrides: true,
  isDetailsViewEnabled: true,
  isHistoryEnabled: true,
  rowLabel: 'accounts',
  searchableColumnNames: [
    VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY,
    VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_DESCRIPTION,
    VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME,
    VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID,
  ],
  isRowOptional: (row: ValueMappingRow) => {
    // We don't require you to fill out for non-fallback rows for approval
    // TODO: DI-7124 handle this more gracefully
    const vendorEntityForRow =
      row[VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY];
    // It will be null for configs with vendor entity but no value, undefined for configs without this concept
    return vendorEntityForRow != null;
  },
  enableDownload: true,
  isSuggestionsEnabled: true,
};

const SupplierCompanyConfiguration: ValueMappingTableConfiguration = {
  configId: SUPPLIER_COMPANY_CONFIG_ID,
  // We don't want to show company_id in the subtitle label, so return null here
  columnPickerKeyOverrides: {
    [VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID]: () => null,
  },
  asyncLoadOutputOptionsForColumn: {
    [VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID]: true,
  },
  // Sort so the biggest sums are at the top (they're the "most important" to map)
  defaultSorting: [
    {
      name: VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND,
      direction: 'desc',
      absolute: true,
    },
  ],
  columnLabelOverrides: {
    [VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID]: 'Company',
    [VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME]:
      'Supplier',
    [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND]:
      'Total spend',
  },
  searchableColumnNames: [
    VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME,
  ],
  rowLabel: 'vendors',
  hasRowFlagging: true,
  isDetailsViewEnabled: true,
  isHistoryEnabled: true,
  isRequiredFilterEnabled: true,
  isRowOptional: (row: ValueMappingRow) => {
    const rawDatasetKinds = row[VALUE_MAPPING_SPECIAL_COLUMN_NAME_DATASET_KIND];
    if (rawDatasetKinds) {
      const datasetKinds = JSON.parse(
        rawDatasetKinds as string
      ) as Array<GQCanonicalDatasetKind>;
      return !datasetKinds.some(
        (kind) => kind && SUPPLIERS_REQUIRED.includes(kind)
      );
    }
    return true;
  },
  primaryKeyColumn:
    VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME,
  enableDownload: true,
  /*
   * TODO(SUP-5955): re-enable company change request on value mapping
  specialOptions: [
    {
      id: VALUE_MAPPING_OUTPUT_OPTION_KEY_SUPPLIER_COMPANY_REQUEST,
      title: 'My company is not listed',
      subtitle:
        "We'll review this request and automatically update your mapping once we've researched the company details you provide.",
      descriptionLong: null,
      isSuggested: false,
      category: null,
      extraData: {},
    },
  ],
  */
};

const MethodologyCustomizationConfigurationBase: Omit<
  ValueMappingTableConfiguration,
  'configId'
> = {
  // NOTE (bryan, CALC-3634): We don't want to show the srid in the subtitle
  // label, so we return undefined here.
  columnPickerKeyOverrides: {
    [METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN]: () => null,
  },
  columnLabelOverrides: {
    [METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN]:
      METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN_LABEL,
  },
  // Note that this is also customized further below - we DON'T enable METHODOLOGY_CUSTOMIZATION_OUTPUT_COLUMN
  // since we can't search the option text right now, just the underlying raw ids and that helps no one
  searchableColumnNames: [
    METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_START_DATE,
    METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_END_DATE,
  ],
  validateOutputOptions(
    row: ValueMappingRecordWithId,
    outputOptions: Array<GQValueMappingOutputOption>
  ) {
    // Don't mark an option as invalid out if our data doesn't look right
    if (
      !(
        METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_START_DATE in row
      ) ||
      !(METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_END_DATE in row)
    ) {
      return undefined;
    }

    // These should be parsed into Date objects already
    const activityStartDate =
      row[METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_START_DATE];
    const activityEndDate =
      row[METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_ACTIVITY_END_DATE];

    const invalidOptions: Array<string> = [];
    const warnings: ValueMappingOutputValidationResult['warnings'] = [];
    const validActivityStartDate = isDate(activityStartDate);
    const validActivityEndDate = isDate(activityEndDate);
    outputOptions.forEach((outputOption: GQValueMappingOutputOption) => {
      const optionStartDateAny = outputOption.extraData?.startDate;
      const optionEndDateAny = outputOption.extraData?.endDate;
      const optionStartDate = optionStartDateAny
        ? DateTime.fromMillis(optionStartDateAny).toJSDate()
        : undefined;
      const optionEndDate = optionEndDateAny
        ? DateTime.fromMillis(optionEndDateAny).toJSDate()
        : undefined;

      // First, let's see if the outputOption is completely invalid because
      // the dates don't overlap at all.
      const optionEndsBeforeActivityBegins =
        optionEndDate &&
        validActivityStartDate &&
        optionEndDate < activityStartDate;
      const optionStartsAfterActivityEnds =
        optionStartDate &&
        validActivityEndDate &&
        optionStartDate > activityEndDate;
      if (optionStartsAfterActivityEnds || optionEndsBeforeActivityBegins) {
        invalidOptions.push(outputOption.id);
        // Return early; no need to generate warnings if it's completely invalid.
        return;
      }

      // A partial-overlap happens when the activity's interval is not
      // completely covered by the option.
      const activityStartsBeforeOptionBegins =
        validActivityStartDate &&
        optionStartDate &&
        activityStartDate < optionStartDate;
      const activityEndsAfterOptionEnds =
        validActivityEndDate &&
        optionEndDate &&
        activityEndDate > optionEndDate;
      if (activityStartsBeforeOptionBegins || activityEndsAfterOptionEnds) {
        warnings.push({
          outputOptionId: outputOption.id,
          label: `This emission factor's dates only partially overlap.`,
        });
      }
    });
    return {
      invalidOptions,
      warnings,
    };
  },

  /**
   * Allows users to internally flag items
   */
  hasRowFlagging: true,
  /**
   * Turns on commenting + files only, not changelog yet
   */
  isDetailsViewEnabled: true,
  /**
   * AKA changelog
   */
  isHistoryEnabled: true,
};

/**
 * This covers the three electricity/building configs that are similar enough to share a base for the searchable columns
 */
const MethodologyCustomizationConfigurationElectricity: Omit<
  ValueMappingTableConfiguration,
  'configId'
> = {
  ...MethodologyCustomizationConfigurationBase,
  searchableColumnNames: [
    METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME,
    ...(MethodologyCustomizationConfigurationBase.searchableColumnNames ?? []),
  ],
};

/**
 * This covers the supplier spend case alone
 */
const MethodologyCustomizationConfigurationSupplierSpend: Omit<
  ValueMappingTableConfiguration,
  'configId'
> = {
  ...MethodologyCustomizationConfigurationBase,
  searchableColumnNames: [
    METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_SUPPLIER_NAME,
    ...(MethodologyCustomizationConfigurationBase.searchableColumnNames ?? []),
  ],
};

/**
 * This configuration is needed for any value mapping CONFIGS.
 * (a simplified core mapping, separate from IDI or any other automatic flows)
 *
 * If you do not fill out any fields, defaults will be used instead.
 *
 * NOTE: we may move configs into BE if we platformize further
 * but it's unknown and dependent on our having many more users first.
 */
export function getTableConfigurationForMappingConfigId(
  valueMappingConfigId: string
): ValueMappingTableConfiguration {
  if (!isMappingKindConfigId(valueMappingConfigId)) {
    throw new BadInputError(
      `No mapping of this id found: ${valueMappingConfigId}. You must fill out ValueMappingTableConfigurations for new value mappings!`
    );
  }

  if (isMethodologyCustomizationValueMappingConfigId(valueMappingConfigId)) {
    switch (valueMappingConfigId) {
      case 'electricity_production_ef_building':
      case 'electricity_t_d_wtt_building':
      case 'electricity_wtt_ef_building':
        return {
          configId: valueMappingConfigId,
          ...MethodologyCustomizationConfigurationElectricity,
        };
      case 'supplier_spend_ef':
        return {
          configId: valueMappingConfigId,
          ...MethodologyCustomizationConfigurationSupplierSpend,
        };
    }
  }

  // For simple hardcoded config id's, just add a case here
  switch (valueMappingConfigId) {
    case FINANCIALS_CONFIG_ID:
      return FinancialsConfiguration;
    case SUPPLIER_COMPANY_CONFIG_ID:
      return SupplierCompanyConfiguration;
    default:
      assertNever(valueMappingConfigId);
  }
}

/*

{
    "__stable_row_id": "srid_1344c2501ff28d7df6f8cf805b373fd45",
    "__group_id": "gid_1344c2501ff28d7df6f8cf805b373fd45",
    "building_identifier": "/VpSAUppXb7ayuLdzjCnzw==",
    "ref_data_pointer": "customer_upload_electricity_production:initial_version.ef_id.srid_1aa26aa6d3ae94fdeb45440f29cc12c06",
    "row_flag": null,
    "building_name": "Transamerica Pyramid",
    "bart_instance_ids": "[\"bart_2aN56Cmba35bWrnviuUw\",\"bart_2aN56CkxVqjcGCJnPUR2\",\"bart_2aN56Cksgz8UBWPA3JbS\",\"bart_2aN56Ck9S3anvWfSfTPt\",\"bart_2aN56Cn3WicftnnXgbVt\",\"bart_2aN56CjqnFmsV27oxPWp\"]",
    "activity_start_date": null,
    "activity_end_date": null,
    "id": "gid_1344c2501ff28d7df6f8cf805b373fd45"
}
*/
